import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PagedFilterMessageTable from "../../components/table/message-table";
import Layout from "../layout";
import { createColumnHelper } from "@tanstack/react-table";
import "bootstrap/dist/css/bootstrap.css";
import { ServiceManager } from "../../services/ServiceManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCircle } from "@fortawesome/free-solid-svg-icons";
import { EscalationPriority, EscalationPriorityStyleMap, LogLevel, LogLevelStyles, Permissions } from "../../app/enums";
import CustomModal from "../../components/modal";
import { Button, Stack } from "react-bootstrap";
import { GUID } from "../../app/common/helper/Guid";
import { NavLink } from "react-router-dom";
import { usePermissionCheck } from "../../app/common/helper/Permissions";
import { useQueryClient } from "@tanstack/react-query";
import { MessageDetailsModal } from "../../components/message-details/MessageDetailsModal";
import { mapMessageToDetails } from "../../components/message-details/messageMapper";
import HUBTooltip from "../../components/tooltip";
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import IMessage from "../../models/Message";

interface IMessageProps {
    StartDate?: any
    EndDate?: any
    Establishment?: any
    Device?: any
    Product?: any
    Package?: any
    DateTime?: any
    LogLevel?: any
    Category?: any
}

export default function Message(props: IMessageProps) {
    const [tableJobs, setTableJobs] = useState<any[]>([]);
    const [continuationTokens, setContinuationTokens] = useState<string[]>([""]);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState<IMessage | null>(null);
    const [selectedGroupID, setSelectedGroupID] = useState<string>("");
    const [selectedGroupRow, setSelectedGroupRow] = useState<any>();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    let CanViewEstablishments: boolean = usePermissionCheck(Permissions.CanViewEstablishments);

    const columnHelper = createColumnHelper<IMessage>();

    const columns = [
        columnHelper.accessor('establishmentName', {
            header: () => <span>Establishment</span>,
            cell: ({ row }) => (
                <div className="long-text" title={row.original.establishmentName as string}>
                    {CanViewEstablishments ? (
                        <NavLink to={`/establishment/${row.original.establishmentID}`}>
                            {row.original.establishmentName}
                        </NavLink>
                    ) : (
                        row.original.establishmentName
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('salesForceExternalID', {
            header: () => <span>External ID</span>,
            cell: ({ row }) => (
                <div className="long-text" title={row.original.establishmentName as string}>
                    <a href={row.original.salesForceUrl} className="underline" target="_blank" rel="noreferrer">
                        {row.original.salesForceExternalID}
                    </a>
                </div>
            ),
        }),
        columnHelper.accessor('deviceName', {
            header: () => <span>Device</span>,
            cell: ({ row }) => <div className="short-text">{row.original.deviceName}</div>,
        }),
        columnHelper.accessor('productName', {
            header: () => <span>Product</span>,
            cell: ({ row }) => <div className="short-text">{row.original.productName}</div>,
        }),
        columnHelper.accessor('packageName', {
            header: () => <span>Package</span>,
            cell: ({ row }) => <div className="short-text">{row.original.packageName}</div>,
        }),
        columnHelper.accessor('dateCreated', {
            header: () => <span>Date/Time</span>,
            cell: ({ row }) => (
                <div className="short-text" title={new Date(row.original.dateCreated as string).toLocaleString()}>
                    <span>{new Date(row.original.dateCreated as string).toLocaleString()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('category', {
            header: () => <span>Category</span>,
            cell: ({ row }) => <div className="short-text">{row.original.category}</div>,
        }),
        columnHelper.accessor('logLevel', {
            header: () => <span>Log Level</span>,
            cell: ({ row }) => (
                <div className="loglevel-box">
                    <div className={`p-1 ps-2 pe-2 logs ${LogLevelStyles[row.original.logLevel]}`}>
                        <FontAwesomeIcon icon={faCircle} size="2xs" className={`${LogLevelStyles[row.original.logLevel]}`} />
                        <span className="ms-2">{LogLevel[row.original.logLevel]}</span>
                    </div>
                </div>
            ),
        }),
        columnHelper.accessor("escalationPriority", {
            header: () => <span>Escalation Priority</span>,
            cell: ({ row }) => {
                const priority = row.original.escalationPriority;

                if (priority == null) {
                    return null;
                }

                const style = EscalationPriorityStyleMap[priority];

                return (
                    <div className="loglevel-box">
                        <div className={`p-1 ps-2 pe-2 logs ${style.className}`}>
                            <FontAwesomeIcon
                                icon={faCircle}
                                size="2xs"
                                className={style.className}
                            />
                            <span className="ms-2">
                                {EscalationPriority[priority]}
                            </span>
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor('flagForHumanReview', {
            header: () => <span>Human Review</span>,
            cell: ({ row }) => {
                const { flagForHumanReview, humanReviewReason } = row.original;
                if (!flagForHumanReview) return <span></span>;

                const tooltipText = humanReviewReason
                    ? `Review Reason: ${humanReviewReason}`
                    : ``;

                return (
                    <div className="flex justify-center items-center h-full">
                        <HUBTooltip message={tooltipText} placement="top">
                            <FontAwesomeIcon
                                className="cursor-pointer"
                                icon={faFlag}
                                color="red"
                                size="lg"
                            />
                        </HUBTooltip>
                    </div>
                );
            },
        }),
        columnHelper.accessor('message', {
            header: () => <span>Message</span>,
            cell: ({ row }) => (
                <div className="message-text">
                    <Button
                        className="btn btn-light crbc-bg-color position-relative"
                        onClick={() => {
                            onRowClick(row);
                        }}
                    >
                        View
                    </Button></div>
            ),
        }),
    ];

    const queryClient = useQueryClient();


    function onRowClick(row: any) {
        const { groupMessageCount, groupID } = row.original;
        if (groupMessageCount > 1) {
            setSelectedGroupID(groupID);
            setSelectedGroupRow(row);
        } else {
            setSelectedMessage(row.original);
            setShowMessageModal(true);
        }
    }

    useEffect(() => {
        if (selectedGroupID) {
            queryClient.invalidateQueries({ queryKey: ["messages"] });
        }
    }, [selectedGroupID, queryClient]);

    useEffect(() => {
        if (!selectedGroupID && selectedMessage) {
            setShowMessageModal(true);
        }
    }, [selectedGroupID, selectedMessage]);

    async function resetContinuationToken() {
        setContinuationTokens([""]);
    }

    async function fetchData(options: {
        startDate?: string;
        endDate?: string;
        pageIndex: number;
        pageSize: number;
        searchValue?: string
        logLevels?: number[];
        categoryName?: string;
        establishmentId?: GUID;
        deviceId?: GUID;
        productId?: GUID;
        packageId?: GUID;
        messageId?: GUID;
        groupId?: string;
    }) {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        // Get Query string Value
        const startDate = params.get('startDate')
        const finishDate = params.get('finishDate')
        const messageId = params.get('messageId')
        const establishmentId = params.get('establishmentId')
        const deviceId = params.get('deviceId')
        const productId = params.get('productId')
        const packageId = params.get('packageId')
        const category = params.get('category')
        const logLevelParam = params.get('logLevel') ? params.get('logLevel')?.split(',').map(Number) : null;
        const logLevelFilter = options.logLevels ? options.logLevels as number[] : logLevelParam as number[];
        const deviceIdOpt = deviceId && deviceId !== "undefined" ? deviceId as GUID : options.deviceId;
        const establishmentIdOpt = establishmentId && establishmentId !== "undefined" ? establishmentId as GUID : options.establishmentId;
        const productIdOpt = productId && productId !== "undefined" ? productId as GUID : options.productId;
        const packageIdOpt = packageId && packageId !== "undefined" ? packageId as GUID : options.packageId;
        const categoryOpt = category ? category as string : options.categoryName;
        // Date Option Set
        const startDateOpt = options.startDate ? options.startDate : startDate as string;
        const finishDateOpt = options.endDate ? options.endDate : finishDate as string;
        const messageIdOpt = messageId && messageId !== "undefined" ? messageId as GUID : options.messageId;
        const continuationToken = continuationTokens[options.pageIndex];
        const groupID = options.groupId;
        //Messages filtering call

        let establishmentResponse: any;
        establishmentResponse = await ServiceManager.MessageService.GetAll(startDateOpt, finishDateOpt, options.pageIndex + 1,
            options.pageSize, continuationToken, logLevelFilter, categoryOpt, establishmentIdOpt, deviceIdOpt, productIdOpt, packageIdOpt, messageIdOpt, groupID)

        if (!continuationTokens.includes(establishmentResponse.data.continuationToken)) {
            setContinuationTokens((prevTokens) => [...prevTokens, establishmentResponse.data.continuationToken]);
        }

        if (groupID && establishmentResponse.data?.data?.length > 0) {
            establishmentResponse.data.data = establishmentResponse.data.data.map((row: any) => ({
                ...row,
                groupMessageCount: 1
            }));
        }

        setTableJobs(establishmentResponse.data);

        return establishmentResponse.data;
    }

    const MessageListHeader = () => {
        return (
            <Stack>
                <p className="heading">Message Details</p>
            </Stack>
        )
    }

    function navigateBack() {
        let fromDashboard = searchParams.get('fromDashboard');
        let establishmentId = searchParams.get('establishmentId');
        if (fromDashboard) {
            navigate(`/`);
        } else {
            navigate(`/establishment/${establishmentId}`);
        }
    }

    function callbackGroup(groupId: any) {
        setSelectedGroupID(groupId);
        if (groupId === null) {
            setSelectedMessage(null);
        }
    }

    return (

        <Layout title="Messages"
            backArrow={
                <FontAwesomeIcon icon={faArrowLeft} onClick={(e) => navigateBack()}
                    className="me-4 fs-5 cursor-pointer" />
            }>
            <CustomModal
                customClass={'message-details-modal-container'}
                isShow={showMessageModal}
                handleClose={() => {
                    setShowMessageModal(false);
                    setSelectedMessage(null);
                }}
                header={<MessageListHeader />}
                size="lg">
                {selectedMessage && (
                    <MessageDetailsModal
                        messageDetails={mapMessageToDetails(selectedMessage)}
                        onClose={() => {
                            setShowMessageModal(false);
                            setSelectedMessage(null);
                        }}
                    />
                )}
            </CustomModal>
            <div className="messages">
                <PagedFilterMessageTable
                    tableKey={"messages"}
                    columns={columns}
                    data={tableJobs}
                    usePagination={true}
                    useSearch={false}
                    pageRecords={10}
                    fnFetchData={fetchData}
                    fnResetContinuationToken={resetContinuationToken}
                    selectedGroupId={selectedGroupID}
                    groupSelectedRow={selectedGroupRow}
                    callbackGroup={callbackGroup}
                />
            </div>
        </Layout>
    );
};